import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { styled, useTheme } from '@mui/material';
import * as React from 'react';
import { FlexBox } from '../box';
import { Button } from '../button';
import { getNextId, HelperText, Wrapper } from './helpers';
export const SignatureSvg = ({ width, height, placeholder, initialState = [], clearLabel, label, onChange, required, disabled, error }) => {
    const svgRef = React.useRef(null);
    const drawingRef = React.useRef(false);
    const linesRef = React.useRef(initialState);
    const currentLineRef = React.useRef([]);
    const [lines, setLines] = React.useState(initialState);
    const [, setTick] = React.useState(0);
    const titleId = getNextId('signature-svg-title-id');
    const errorId = getNextId('signature-svg-error-id');
    const hasError = Boolean(error);
    const { spacing } = useTheme();
    const createPoint = (e, svg) => {
        const rect = svg.getBoundingClientRect();
        return { x: e.clientX - rect.left, y: e.clientY - rect.top };
    };
    const commitChange = () => {
        if (!svgRef.current) {
            return;
        }
        const svgElement = svgRef.current;
        const serializer = new XMLSerializer();
        const svgString = serializer.serializeToString(svgElement);
        const base64 = `${btoa(svgString)}`;
        onChange({
            lines: linesRef.current,
            dataUrl: base64
        });
    };
    const startDrawing = (point) => {
        if (disabled) {
            return;
        }
        drawingRef.current = true;
        currentLineRef.current = [point];
        setTick((t) => t + 1);
    };
    const draw = (point) => {
        if (!drawingRef.current) {
            return;
        }
        currentLineRef.current.push(point);
        setTick((t) => t + 1);
    };
    const stopDrawing = () => {
        if (!drawingRef.current) {
            return;
        }
        drawingRef.current = false;
        if (currentLineRef.current.length > 0) {
            linesRef.current = [...linesRef.current, currentLineRef.current];
            setLines(linesRef.current);
            currentLineRef.current = [];
            commitChange();
        }
    };
    const handleMouseDown = (e) => {
        if (svgRef.current) {
            startDrawing(createPoint(e.nativeEvent, svgRef.current));
        }
    };
    const handleMouseMove = (e) => {
        if (svgRef.current) {
            draw(createPoint(e.nativeEvent, svgRef.current));
        }
    };
    const handleTouchStart = (e) => {
        if (svgRef.current) {
            startDrawing(createPoint(e.touches[0], svgRef.current));
        }
    };
    const handleTouchMove = (e) => {
        if (svgRef.current) {
            draw(createPoint(e.touches[0], svgRef.current));
        }
    };
    const handleClear = () => {
        linesRef.current = [];
        currentLineRef.current = [];
        setLines([]);
        onChange(undefined);
    };
    const innerHeightMargin = parseFloat(spacing(3));
    const innerWidthMargin = parseFloat(spacing(4));
    const hasSignature = lines.length > 0 || currentLineRef.current.length > 0;
    const accessibleState = hasSignature
        ? 'written signature'
        : 'empty signature';
    const groupAriaLabel = `${label || 'Signature'} drawing area - ${accessibleState}`;
    return (_jsxs(Wrapper, { width: width, height: height, maxHeight: height, padding: [3, 2], "aria-label": groupAriaLabel, role: "group", stack: true, gap: 2, children: [_jsxs(FloatingLabel, { disabled: disabled, error: hasError, children: [label, " ", required && _jsx("span", { "aria-hidden": "true", children: " *" })] }), _jsxs(SignatureArea, { disabled: disabled, width: width - innerWidthMargin, height: height - innerHeightMargin, error: hasError, "aria-invalid": hasError, children: [_jsxs(Svg, { xmlns: "http://www.w3.org/2000/svg", shapeRendering: "geometricPrecision", width: width, height: height, viewBox: `0 0 ${width} ${height}`, ref: svgRef, onMouseDown: handleMouseDown, onMouseMove: handleMouseMove, onMouseUp: stopDrawing, onMouseLeave: stopDrawing, onTouchStart: handleTouchStart, onTouchMove: handleTouchMove, onTouchEnd: stopDrawing, role: "img", "aria-labelledby": titleId, "aria-describedby": errorId, children: [_jsx("title", { id: titleId, children: hasSignature ? 'Written signature' : 'Empty signature' }), lines.map((points, i) => (_jsx(Line, { points: points }, i))), currentLineRef.current.length > 0 && (_jsx(Line, { points: currentLineRef.current }))] }), placeholder &&
                        lines.length === 0 &&
                        currentLineRef.current.length === 0 && (_jsx(Placeholder, { disabled: disabled, error: hasError, children: placeholder })), !disabled && !error && (_jsx(ClearButton, { onClick: handleClear, children: clearLabel }))] }), hasError && (_jsx(HelperText, { id: errorId, error: true, children: error }))] }));
};
const SignatureArea = styled(FlexBox)(({ theme, disabled, error }) => ({
    border: '1px dashed',
    borderColor: error
        ? theme.palette.error._states['outlined-border']
        : theme.components?.input?.outlined?.['enabled-border'],
    borderRadius: theme.shape.borderRadius,
    position: 'relative',
    cursor: disabled ? 'default' : 'crosshair',
    touchAction: 'none',
    pointerEvents: disabled ? 'none' : 'auto',
    overflow: 'hidden',
    background: error
        ? theme.components?.alert?.error?.background
        : theme.palette.background.paper
}));
const Svg = styled('svg')({ touchAction: 'none' });
const Placeholder = styled('label')(({ theme, disabled, error }) => ({
    position: 'absolute',
    color: disabled
        ? theme.palette.text.disabled
        : error
            ? theme.palette.error.main
            : theme.palette.text.secondary,
    fontSize: theme.components?.input?.valuer?.fontSize,
    fontWeight: theme.components?.input?.valuer?.fontWeight,
    pointerEvents: 'none',
    margin: 'auto',
    bottom: 0,
    top: 0,
    right: 0,
    left: 0,
    height: 'max-content',
    width: 'max-content'
}));
const ClearButton = styled(Button)(({ theme }) => ({
    position: 'absolute',
    bottom: theme.spacing(2),
    left: 0,
    right: 0,
    margin: 'auto',
    zIndex: 1,
    width: 'max-content'
}));
const FloatingLabel = styled('p')(({ theme, disabled, error }) => ({
    position: 'absolute',
    top: theme.spacing(0.5),
    left: theme.spacing(3),
    background: `linear-gradient(to bottom, transparent 0%, transparent 50%, ${error ? theme.components?.alert?.error?.background : theme.palette.background.paper}  50%, ${error ? theme.components?.alert?.error?.background : theme.palette.background.paper}  100%)`,
    fontSize: theme.components?.input?.label?.fontSize,
    fontWeight: theme.components?.input?.label?.fontWeight,
    color: disabled
        ? theme.palette.text.disabled
        : error
            ? theme.palette.error.main
            : theme.palette.text.secondary,
    paddingLeft: theme.spacing(0.5),
    paddingRight: theme.spacing(0.5),
    zIndex: 99,
    transform: 'translateY(-6%)'
}));
export const Line = ({ points }) => {
    const theme = useTheme();
    return (_jsx("path", { fill: "none", stroke: theme.palette.primary.inverseText, strokeWidth: "3px", strokeLinejoin: "round", strokeLinecap: "round", d: `M ${points.map((p) => `${p.x} ${p.y}`).join(' L ')}` }));
};
